import { useEffect, useState } from 'react';
import { whatsappAuthorize } from '../../api/endpoints';
import type { Inbox, WhatsappSignupConfig } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { FacebookIcon } from '../../lib/brandIcons';
import { runEmbeddedSignup, setupFacebookSdk } from '../../lib/whatsappSignup';

type Phase = 'idle' | 'popup' | 'connecting';

/**
 * The minimum-steps WhatsApp connect: Meta's embedded-signup popup. One click
 * opens Facebook's own dialog (sign in, pick or create the business, WABA and
 * number); the popup hands back an auth code + WABA ids and the server does the
 * rest — token exchange, number discovery, webhook registration, template sync.
 *
 * The caller owns the surrounding page; this panel only reports the created
 * inbox via `onDone`.
 */
export function WhatsappEmbedded({
  config,
  onDone,
}: {
  config: WhatsappSignupConfig;
  onDone: (inbox: Inbox) => void;
}) {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  // Meta names the screen a tenant backed out of. Saying it back is the only
  // way they learn which step to expect when they try again.
  const [stoppedAt, setStoppedAt] = useState<string | null>(null);

  // Warm the FB SDK while the admin reads the page, so the click handler opens
  // the popup immediately — a popup opened late trips blockers.
  useEffect(() => {
    void setupFacebookSdk(config).catch(() => {
      // Ignored: runEmbeddedSignup retries the load and surfaces the error.
    });
  }, [config]);

  const start = async () => {
    setError(null);
    setStoppedAt(null);
    setPhase('popup');
    try {
      const result = await runEmbeddedSignup(config);
      if (result.status === 'cancelled') {
        setStoppedAt(result.step);
        setPhase('idle'); // closed the popup — not an error
        return;
      }
      setPhase('connecting');
      const inbox = await whatsappAuthorize({
        code: result.code,
        wabaId: result.wabaId,
        ...(result.phoneNumberId ? { phoneNumberId: result.phoneNumberId } : {}),
        ...(result.businessId ? { businessId: result.businessId } : {}),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onDone(inbox);
    } catch (e) {
      setPhase('idle');
      setError(e instanceof Error ? e.message : 'The WhatsApp signup could not be completed.');
    }
  };

  if (phase === 'connecting') {
    return (
      <div className="card card-pad connect-panel">
        <Spinner large />
        <p>
          <strong>Finishing the connection…</strong>
        </p>
        <p className="connect-panel-hint">
          Confirming access with Meta, registering the webhook and syncing your message templates —
          this can take up to a minute.
        </p>
      </div>
    );
  }

  return (
    <div className="card card-pad connect-form wa-embed">
      {error && <ErrorNote error={error} />}
      {stoppedAt !== null && !error && (
        <Note tone="info">
          Signup stopped at the {stoppedAt.toLowerCase().replace(/_/g, ' ')} step. Nothing was
          saved — start again when you're ready.
        </Note>
      )}
      <p className="wa-embed-copy">
        Sign in with the Facebook account that manages your WhatsApp Business. Meta walks you
        through picking (or creating) the business, WhatsApp account and phone number — nothing to
        copy out of dashboards.
      </p>
      <Field label="Inbox name (optional)" hint="Defaults to your WhatsApp business name.">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Support"
          disabled={phase === 'popup'}
        />
      </Field>
      <div className="connect-form-actions wa-embed-actions">
        <button
          type="button"
          className="btn btn-primary wa-embed-btn"
          onClick={() => void start()}
          disabled={phase === 'popup'}
        >
          {phase === 'popup' ? <Spinner /> : <FacebookIcon size={15} tint="currentColor" />}
          {phase === 'popup' ? 'Waiting for the Facebook window…' : 'Continue with Facebook'}
        </button>
      </div>
    </div>
  );
}
